import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';

import { createSpacingFields, PX_AI_SCHEMA } from '../fields/spacing-fields';
import { selectField } from '../fields/widgets/select-field';
import { ContainerTextColorField } from '../fields/widgets/container-text-color-field';
import { NumberField } from '../fields/widgets/number-field';
import { BorderColorField } from '../fields/widgets/border-color-field';
import { BORDER_COLOR_AI_SCHEMA, borderStyle, DEFAULT_BORDER } from '../fields/border';
import type { BorderValue } from '../fields/border';
import { CONTAINER_TEXT_COLOR_AI_SCHEMA, containerColorStyle, INHERIT_COLOR } from '../fields/container-colors';
import { BLOCK_SPACING, CONTAINER_WIDTHS, lookup, spacingToCss } from '../fields/tokens';
import type { BoxSpacing } from '../fields/tokens';
import { BackgroundField, backgroundStyle, BACKGROUND_AI_SCHEMA, DEFAULT_BACKGROUND } from '../fields';
import type { BackgroundValue } from '../fields';

import { starterChild } from './slot';
import type { SlotProps, StarterChild } from './slot';

/** Vertical rhythm between the section's direct children. */
const CHILD_GAP = BLOCK_SPACING.sm;

/** Section's own padding default: roomier than a leaf block, so it reads as a page band. */
const DEFAULT_PADDING: BoxSpacing = { top: '48px', right: '24px', bottom: '48px', left: '24px' };

export type SectionProps = {
  /**
   * Puck injects this slot component at render time. `defaultProps.content` holds the starter
   * layout's child data instead (an array), which Puck consumes at insert time and replaces with
   * the injected component before `render` ever sees it.
   */
  content?: React.FC<SlotProps>;
  background: BackgroundValue;
  /**
   * Pushes a text color onto every descendant via a scoped custom property. Typed `string`, not
   * `ContainerTextColor`, because the editor widget offers a custom hex escape hatch alongside the
   * named roles -- see `containerColorStyle`, which resolves either form.
   */
  textColor: string;
  /**
   * Required, not optional: this is the *authoring* contract, so no caller may omit it and there
   * is no call-site conditional in `render` below.
   *
   * That is deliberately NOT a claim that `padding` is always present at runtime. `Section`
   * predates this field and Puck does not merge `defaultProps` on the production `<Render>` path,
   * so an already-published section can still reach `render` with `padding` genuinely absent. Safe
   * because `spacingToCss` (called below as `spacingToCss(padding)`) internally does
   * `spacing ?? NO_SPACING` -- a guard deliberately left in place rather than removed, since it's
   * shared with Heading/Paragraph/Button/Image, whose data wasn't in scope here.
   */
  padding: BoxSpacing;
  /** A `CONTAINER_WIDTHS` key, or a raw CSS length carried over from before this was a token. */
  maxWidth: string;
  /**
   * Required, not optional: this is the *authoring* contract, so no caller may omit it and there
   * is no `?? DEFAULT_BORDER` fallback in `render` below.
   *
   * That is deliberately NOT a claim that `border` is always present at runtime. `Section`
   * predates this field (#21596/#21708) and Puck does not merge `defaultProps` on the production
   * `<Render>` path, so an already-published Section really does reach `render` with `border`
   * absent -- which is why `borderStyle` accepts `undefined` and returns `{}` for it. **Do not
   * remove that guard on the strength of this prop being typed required**; with no error boundary
   * on the renderer, dereferencing an absent `border` white-screens the whole page, and the editor
   * would still look correct because it *does* inject the default. See `border.ts`'s `borderStyle`
   * doc comment and the "border is absent from stored props" test below.
   */
  border: BorderValue;
};

/**
 * Resolves the stored max width.
 *
 * Accepts a token key or a literal, mirroring `resolveTextColor`: pages authored before this was a
 * token hold raw lengths like `'800px'`, and silently re-capping them to a preset would visibly
 * reflow live content.
 */
function resolveMaxWidth(value: string): string {
  return lookup(CONTAINER_WIDTHS, value, value);
}

// Starter layout: a heading and paragraph give new authors something to act on immediately.
//
// Each child is built from its own component's `defaultProps` via `starterChild()`, overriding only
// the copy. That completeness is load-bearing, not tidiness: Puck merges `defaultProps` for the
// inserted Section but not for its slot children, and the field panel reads child props unmerged,
// so a prop left absent here shows an empty control and corrupts `object`-field edits. See
// `StarterChild` in ./slot for the full mechanism, and `editor/default-layout-parity.test.tsx`
// for the test that enforces it.
//
// id is intentionally absent — populateIds() stamps { id: generated, ...item.props }, so a
// hardcoded id would win and produce duplicate ids across every inserted Section instance.
const SECTION_STARTER_CHILDREN: StarterChild[] = [
  starterChild('Heading', { text: '<p>Your section heading</p>' }),
  starterChild('Paragraph', { text: '<p>Add your content here.</p>' }),
];

/** Full-width container that groups content in a padded, optionally capped column. */
export const sectionConfig: ComponentConfig<SectionProps> = {
  label: 'Section',
  ai: {
    instructions:
      "A full-width container that groups content. IMPORTANT: Always add child components (Heading, Text, Image, Button) INTO this component's content slot — never leave it empty. A Section without children is useless.",
  },
  fields: {
    content: { type: 'slot' },
    background: {
      type: 'custom',
      label: 'Background',
      render: BackgroundField,
      ai: {
        schema: BACKGROUND_AI_SCHEMA as Record<string, unknown>,
        instructions:
          "Background styling. Set color to a surface role ('none', 'surface', 'muted') so it follows merchant branding; use a hex value only when a specific brand color is required. Optionally set image URL with size/position/repeat.",
      },
    },
    textColor: {
      type: 'custom',
      label: 'Text color',
      render: ContainerTextColorField,
      ai: {
        schema: (CONTAINER_TEXT_COLOR_AI_SCHEMA as unknown) as Record<string, unknown>,
        instructions:
          "Text color applied to everything inside this section. Leave as 'inherit' unless the section has a dark or colored background, where 'inverted' keeps the copy readable. A hex value can be used when a specific brand color is required.",
      },
    },
    // selectField rather than a native `select`: sections authored before this was a token hold a raw
    // length like '800px', and a native select with no matching option silently displays the first
    // one -- claiming 'Narrow' for a page rendering at 800px. The widget surfaces the stored value
    // instead.
    maxWidth: selectField(
      'Max width',
      [
        { label: 'Narrow', value: 'narrow' },
        { label: 'Normal', value: 'normal' },
        { label: 'Wide', value: 'wide' },
        { label: 'Full width', value: 'none' },
      ],
      "Caps the section container's width. 'narrow' (~768px) suits a focused column; 'normal' (~992px) is a comfortable mid-width band; 'wide' tracks the storefront's configured site width so the section aligns with the header and product grid; 'none' is full-bleed."
    ),
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: createSpacingFields(),
    },
    border: {
      type: 'object',
      label: 'Border',
      objectFields: {
        color: {
          type: 'custom',
          label: 'Color',
          render: BorderColorField,
          ai: {
            schema: BORDER_COLOR_AI_SCHEMA as Record<string, unknown>,
            instructions:
              "Either 'none' (no border), 'theme' (the storefront's standard hairline border color), or a hex value. Prefer 'theme' or 'none'.",
          },
        },
        width: {
          type: 'custom',
          label: 'Width',
          // Second arg keeps the displayed number equal to `borderStyle`'s own fallback for an
          // absent width, so the panel cannot report 0 while the page renders 1px.
          render: NumberField('px', DEFAULT_BORDER.width),
          ai: { schema: PX_AI_SCHEMA, instructions: 'Border width, e.g. 1px.' },
        },
      },
    },
  },
  defaultProps: {
    background: DEFAULT_BACKGROUND,
    textColor: INHERIT_COLOR,
    padding: DEFAULT_PADDING,
    maxWidth: 'none',
    border: DEFAULT_BORDER,
    content: (SECTION_STARTER_CHILDREN as unknown) as React.FC<SlotProps>,
  },
  render: ({ content: slotContent, background, textColor, padding, maxWidth, border }) => {
    if (!slotContent) return <></>;
    const Slot = slotContent;

    return (
      <div
        style={{
          ...backgroundStyle(background),
          ...containerColorStyle(textColor),
          ...borderStyle(border),
          padding: spacingToCss(padding),
          width: '100%',
        }}
      >
        <div style={{ maxWidth: resolveMaxWidth(maxWidth), margin: '0 auto' }}>
          <Slot minEmptyHeight={120} style={{ display: 'flex', flexDirection: 'column', gap: CHILD_GAP }} />
        </div>
      </div>
    );
  },
};
